import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, TextField, Button, Typography, Box, Grid } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';

const TrainingSetModal = ({ isOpen, onClose, onSave, setsCount, workoutId, trainingId, userId }) => {
  const [initialValues, setInitialValues] = useState({ sets: [] });

  useEffect(() => {
    if (setsCount > 0) {
      const setsData = Array.from({ length: setsCount }, (_, index) => ({
        setNumber: index + 1,
        totalWeightKg: '',
        repetitions: '',
        distanceKm: '',
        timeMinutes: '',
        caloriesBurned: '',
      }));
      setInitialValues({ sets: setsData });
    } else {
      setInitialValues({
        sets: [
          {
            setNumber: 0,
            totalWeightKg: '',
            repetitions: '',
            distanceKm: '',
            timeMinutes: '',
            caloriesBurned: '',
          },
        ],
      });
    }
  }, [setsCount]);

  const validationSchema = Yup.object().shape({
    sets: Yup.array().of(
      Yup.object().shape({
        setNumber: setsCount > 0 ? Yup.number().required('Set number is required') : Yup.number(),
        totalWeightKg: setsCount > 0 ? Yup.number().required('Total weight is required') : Yup.number(),
        repetitions: setsCount > 0 ? Yup.number().required('Repetitions are required') : Yup.number(),
        distanceKm: Yup.number(),
        timeMinutes: Yup.number(),
        caloriesBurned: Yup.number(),
      })
    ),
  });

  const handleSubmit = (values) => {
    const updatedSets = values.sets.map((set) => ({
      ...set,
      setNumber: parseFloat(set.setNumber) || 0,
      totalWeightKg: parseFloat(set.totalWeightKg) || 0,
      repetitions: parseInt(set.repetitions) || 0,
      distanceKm: parseFloat(set.distanceKm) || 0,
      timeMinutes: parseFloat(set.timeMinutes) || 0,
      caloriesBurned: parseFloat(set.caloriesBurned) || 0,
    }));

    const dataToSave = {
      currentWorkoutId: workoutId,
      trainingId: trainingId,
      workoutDetails: updatedSets,
      userId: userId,
      isUpdated: true
    };
   
    onSave(dataToSave);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          margin: 'auto',
          marginTop: '100px',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          {setsCount > 0 ? 'Enter Set Information' : 'Enter Workout Details'}
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <FieldArray name="sets">
                {() => (
                  <>
                    {values.sets.map((set, index) => (
                      <Box key={index} sx={{ marginBottom: '20px' }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {setsCount > 0 ? `Set ${index + 1}` : 'Workout Details'}
                        </Typography>
                        <Grid container spacing={2}>
                          {setsCount > 0 && (
                            <>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Set Number"
                                  variant="outlined"
                                  fullWidth
                                  name={`sets[${index}].setNumber`}
                                  value={set.setNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.sets &&
                                    touched.sets[index]?.setNumber &&
                                    Boolean(errors.sets?.[index]?.setNumber)
                                  }
                                  helperText={
                                    touched.sets &&
                                    touched.sets[index]?.setNumber &&
                                    errors.sets?.[index]?.setNumber
                                  }
                                  disabled
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Total Weight (Kg)"
                                  variant="outlined"
                                  fullWidth
                                  name={`sets[${index}].totalWeightKg`}
                                  value={set.totalWeightKg}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.sets &&
                                    touched.sets[index]?.totalWeightKg &&
                                    Boolean(errors.sets?.[index]?.totalWeightKg)
                                  }
                                  helperText={
                                    touched.sets &&
                                    touched.sets[index]?.totalWeightKg &&
                                    errors.sets?.[index]?.totalWeightKg
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Repetitions"
                                  variant="outlined"
                                  fullWidth
                                  name={`sets[${index}].repetitions`}
                                  value={set.repetitions}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.sets &&
                                    touched.sets[index]?.repetitions &&
                                    Boolean(errors.sets?.[index]?.repetitions)
                                  }
                                  helperText={
                                    touched.sets &&
                                    touched.sets[index]?.repetitions &&
                                    errors.sets?.[index]?.repetitions
                                  }
                                />
                              </Grid>
                            </>
                          )}
                          {setsCount === 0 && (
                            <>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Distance (Km)"
                                  variant="outlined"
                                  fullWidth
                                  name={`sets[${index}].distanceKm`}
                                  value={set.distanceKm}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.sets &&
                                    touched.sets[index]?.distanceKm &&
                                    Boolean(errors.sets?.[index]?.distanceKm)
                                  }
                                  helperText={
                                    touched.sets &&
                                    touched.sets[index]?.distanceKm &&
                                    errors.sets?.[index]?.distanceKm
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Time (Minutes)"
                                  variant="outlined"
                                  fullWidth
                                  name={`sets[${index}].timeMinutes`}
                                  value={set.timeMinutes}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.sets &&
                                    touched.sets[index]?.timeMinutes &&
                                    Boolean(errors.sets?.[index]?.timeMinutes)
                                  }
                                  helperText={
                                    touched.sets &&
                                    touched.sets[index]?.timeMinutes &&
                                    errors.sets?.[index]?.timeMinutes
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Calories Burned"
                                  variant="outlined"
                                  fullWidth
                                  name={`sets[${index}].caloriesBurned`}
                                  value={set.caloriesBurned}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.sets &&
                                    touched.sets[index]?.caloriesBurned &&
                                    Boolean(errors.sets?.[index]?.caloriesBurned)
                                  }
                                  helperText={
                                    touched.sets &&
                                    touched.sets[index]?.caloriesBurned &&
                                    errors.sets?.[index]?.caloriesBurned
                                  }
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </>
                )}
              </FieldArray>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <Button onClick={onClose} variant="outlined" color="secondary">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Save
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

TrainingSetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  setsCount: PropTypes.number.isRequired,
  workoutId: PropTypes.number.isRequired,
  trainingId: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired
};

export default TrainingSetModal;
